/**
 * Cleaning for the recommendation evidence quote shown on a run.
 *
 * Two independent problems, in order:
 *
 * 1. Persisted evidence can carry a leaked unit-id marker (`u3:9c87bdd4`, or `u3:9c87bdd4.r2`
 *    for a table sub-unit). Persistence is being fixed server-side, but historical runs keep the
 *    marker until they are backfilled, so the display strips it defensively.
 * 2. The evidence is often a sentence of prose wrapped around the actual quotation. When a
 *    quotation is present we show only what is inside it — the surrounding prose is the model
 *    narrating, not the answer speaking.
 */

/** `u<digits>:<8 hex>` optionally followed by `.r<digits>` for a table row sub-unit. */
const UNIT_ID_MARKER = /\bu\d+:[0-9a-f]{8}(?:\.r\d+)?\b/g;

/**
 * Removes leaked unit-id markers along with the punctuation that immediately attached them to the
 * text, then collapses the whitespace they leave behind. Idempotent: text with no marker comes
 * back byte-identical apart from pre-existing double spaces being preserved.
 */
export function stripUnitIdMarkers(text: string): string {
  if (!text) {
    return '';
  }

  if (!new RegExp(UNIT_ID_MARKER.source).test(text)) {
    return text;
  }

  return (
    text
      // Eat the separator that FOLLOWS the marker ("u3:9c87bdd4: foo" loses the colon, "u7:… - foo"
      // loses the dash). Punctuation *before* the marker is left alone: in "first, u2:…: second"
      // the comma belongs to "first", not to the marker.
      .replace(new RegExp(`\\s*${UNIT_ID_MARKER.source}\\s*[:,\\-]?\\s*`, 'g'), ' ')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/**
 * Quote styles we recognise, as [opener, closer] pairs. Order matters only in that single-quote
 * pairs are considered last (see `extractQuotedText`) — an apostrophe is far more common than a
 * single-quoted passage, and mistaking one for the other mangles the text.
 */
const DOUBLE_QUOTE_PAIRS: readonly (readonly [string, string])[] = [
  ['“', '”'], // “ ”  typographic double
  ['„', '“'], // „ “  German low-high
  ['„', '”'], // „ ”  German low-high, straight-ish closer
  ['«', '»'], // « »  guillemets
  ['‹', '›'], // ‹ ›  single guillemets
  ['"', '"'], // straight double
];

const SINGLE_QUOTE_PAIRS: readonly (readonly [string, string])[] = [
  ['‘', '’'], // ‘ ’  typographic single
  ["'", "'"], // straight single
];

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** The longest content found between any matching pair of the given style, or ''. */
function longestBetween(text: string, pairs: readonly (readonly [string, string])[], singleQuoteRules: boolean): string {
  let longest = '';

  for (const [opener, closer] of pairs) {
    // Non-greedy body, so several quotes in one string are found separately rather than merged
    // into one span running from the first opener to the last closer.
    const body = `([\\s\\S]*?)`;
    const pattern = singleQuoteRules
      ? // An opener only counts at the start or after whitespace, and a closer only before
        // whitespace, punctuation or the end — so an in-word apostrophe can never open or close.
        new RegExp(`(?:^|(?<=\\s))${escapeRegExp(opener)}${body}${escapeRegExp(closer)}(?=$|[\\s.,;:!?)\\]])`, 'g')
      : new RegExp(`${escapeRegExp(opener)}${body}${escapeRegExp(closer)}`, 'g');

    for (const match of text.matchAll(pattern)) {
      const content = match[1].trim();
      if (content.length > longest.length) {
        longest = content;
      }
    }
  }

  return longest;
}

/**
 * The content of the quotation inside `text`, or '' when there is none.
 *
 * Double-style quotes are considered first and win outright; single-style pairs are only tried
 * when no double-style quote matched, and then under stricter boundary rules. Among candidates of
 * the style that matched, the longest wins.
 */
export function extractQuotedText(text: string): string {
  if (!text) {
    return '';
  }

  const double = longestBetween(text, DOUBLE_QUOTE_PAIRS, false);
  if (double) {
    return double;
  }

  return longestBetween(text, SINGLE_QUOTE_PAIRS, true);
}

/**
 * What to render for a run's `explicitRecommendation.evidence`: markers stripped, and reduced to
 * the quotation when the text contains one. Text with no quotation is shown in full.
 */
export function formatRecommendationEvidence(evidence?: string | null): string {
  const stripped = stripUnitIdMarkers(String(evidence || '').trim());
  if (!stripped) {
    return '';
  }

  return extractQuotedText(stripped) || stripped;
}
